/**
 * Agent-portal voice assistant — the mic button bottom-right of the agent
 * portal. The agent talks to it ("what's the status of TKT-1042", "which of
 * my tickets are high priority", "create a ticket for…"); it answers by voice
 * and shows the transcript + every action it took.
 *
 * Shown only when the admin enabled it for this user (GET /agent-assistant/config).
 * Audio goes browser ↔ OpenAI Realtime over WebRTC; tool calls go to
 * POST /agent-assistant/tool, which runs them as this agent (no delete).
 * Disabled while the softphone is on a call (one mic, and the customer
 * must not hear the assistant).
 */
import { useCallback, useEffect, useRef, useState, type JSX } from 'react'
import { AudioLines, Bot, CheckCircle2, Loader2, Mic, MicOff, User, X, XCircle } from 'lucide-react'
import { api } from '../../api/client.js'
import { OpenAiBrowserCall } from '../client-portal/voice/openai-browser-call.js'

interface Config { available: boolean; tools?: { name: string; label: string }[] }
type Phase = 'idle' | 'connecting' | 'live' | 'error'
type Line = { id: string; kind: 'you' | 'assistant' } & { text: string }
type Action = { id: string; kind: 'action'; label: string; ok: boolean | null; pending: boolean }
type Entry = Line | Action

/** End the session after this long with nobody speaking — it's billed per minute. */
const IDLE_END_MS = 3 * 60_000

const ACTION_LABEL: Record<string, string> = {
  get_ticket_status: 'Checked ticket',
  list_tickets: 'Listed tickets',
  create_ticket: 'Create ticket',
  add_note: 'Add note',
  reply_to_ticket: 'Reply to customer',
  send_email: 'Send email',
  search_knowledge: 'Searched knowledge',
}

export default function AgentVoiceAssistant({ onCall }: { onCall: boolean }): JSX.Element | null {
  const [cfg, setCfg] = useState<Config | null>(null)
  const [open, setOpen] = useState(false)
  const [phase, setPhase] = useState<Phase>('idle')
  const [error, setError] = useState<string | null>(null)
  const [speaking, setSpeaking] = useState(false)
  const [level, setLevel] = useState(0)
  const [muted, setMuted] = useState(false)
  const [entries, setEntries] = useState<Entry[]>([])
  const callRef = useRef<OpenAiBrowserCall | null>(null)
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    api.get<Config>('/agent-assistant/config').then(setCfg).catch(() => setCfg({ available: false }))
  }, [])

  useEffect(() => { listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' }) }, [entries])

  const stop = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current)
    callRef.current?.stop()
    callRef.current = null
    setPhase('idle')
    setSpeaking(false)
    setLevel(0)
    setMuted(false)
  }, [])

  const bumpIdle = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current)
    idleTimer.current = setTimeout(() => stop(), IDLE_END_MS)
  }, [stop])

  // A softphone call takes the mic — end the assistant session.
  useEffect(() => { if (onCall && callRef.current) stop() }, [onCall, stop])
  useEffect(() => () => stop(), [stop])

  const start = async (): Promise<void> => {
    if (callRef.current || onCall) return
    setError(null)
    setEntries([])
    setPhase('connecting')
    const call = new OpenAiBrowserCall({
      onStarted: () => { setPhase('live'); bumpIdle() },
      onCaption: () => undefined,
      onTurn: (role, text, itemId) => {
        bumpIdle()
        setEntries((xs) => [...xs, { id: itemId || `${Date.now()}-${xs.length}`, kind: role === 'caller' ? 'you' : 'assistant', text }])
      },
      onSpeaking: setSpeaking,
      onLevel: setLevel,
      onToolCall: async (name, args) => {
        bumpIdle()
        const id = `${Date.now()}-${name}`
        setEntries((xs) => [...xs, { id, kind: 'action', label: ACTION_LABEL[name] ?? name, ok: null, pending: true }])
        try {
          const r = await api.post<{ result: Record<string, unknown> }>('/agent-assistant/tool', { name, args })
          const waiting = r.result['needsConfirmation'] === true
          const ok = r.result['ok'] !== false && r.result['found'] !== false
          setEntries((xs) => xs.map((e) => (e.id === id && e.kind === 'action'
            ? { ...e, pending: false, ok: waiting ? null : ok, label: waiting ? `${e.label} — waiting for your OK` : e.label }
            : e)))
          return r.result
        } catch {
          setEntries((xs) => xs.map((e) => (e.id === id && e.kind === 'action' ? { ...e, pending: false, ok: false } : e)))
          return { ok: false, error: 'TOOL_FAILED', note: 'The action failed. Tell the agent briefly.' }
        }
      },
      onClosed: () => { callRef.current = null; setPhase('idle'); setSpeaking(false) },
    }, { record: false })
    callRef.current = call
    try {
      const offer = await call.prepare()
      const r = await api.post<{ sdp: string; greeting: string }>('/agent-assistant/session', {
        sdp: offer,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
      await call.start(r.sdp, r.greeting)
    } catch (err) {
      call.stop()
      callRef.current = null
      setPhase('error')
      const msg = err instanceof Error ? err.message : ''
      setError(/Permission|NotAllowed/i.test(msg) ? 'Microphone access was blocked. Allow the microphone for this site and try again.' : (msg || 'Could not start the assistant.'))
    }
  }

  if (!cfg?.available) return null

  const live = phase === 'live'
  const status = phase === 'connecting' ? 'Connecting…' : live ? (speaking ? 'Speaking…' : muted ? 'Muted' : 'Listening…') : phase === 'error' ? 'Not connected' : 'Ready'

  return (
    <>
      {open && (
        <div className="fixed bottom-[88px] right-5 z-[880] flex max-h-[min(560px,calc(100vh-120px))] w-[380px] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-[3px] bg-white shadow-[0_16px_48px_-12px_rgba(15,23,42,0.35)] ring-1 ring-slate-200" role="dialog" aria-label="Voice assistant">
          <header className="flex items-center gap-2.5 border-b border-slate-100 px-4 py-3">
            <span className={`flex h-8 w-8 items-center justify-center rounded-full ${live ? 'bg-blue-600 text-white' : 'bg-blue-50 text-blue-600'}`}><AudioLines className="h-4 w-4" /></span>
            <div className="min-w-0 flex-1">
              <p className="text-[13.5px] font-semibold text-slate-900">Voice assistant</p>
              <p className="text-[11.5px] text-slate-500">{status}</p>
            </div>
            <button type="button" aria-label="Close" className="rounded-[3px] p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" onClick={() => { stop(); setOpen(false) }}>
              <X className="h-4 w-4" />
            </button>
          </header>

          <div ref={listRef} className="flex-1 space-y-2 overflow-y-auto px-4 py-3">
            {entries.length === 0 && (
              <div className="py-4 text-[12.5px] text-slate-500">
                {onCall ? 'Not available while you are on a call.' : live ? 'Say what you need, for example:' : 'Press Start and talk. For example:'}
                {!onCall && (
                  <ul className="mt-2 space-y-1 text-slate-600">
                    <li>“What’s the status of ticket 1042?”</li>
                    <li>“Which of my tickets are high priority?”</li>
                    {cfg.tools?.some((t) => t.name === 'create_ticket') && <li>“Create a ticket for a billing complaint.”</li>}
                    {cfg.tools?.some((t) => t.name === 'send_email') && <li>“Send an email to…”</li>}
                  </ul>
                )}
              </div>
            )}
            {entries.map((e) => e.kind === 'action' ? (
              <div key={e.id} className="flex justify-center">
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ${e.pending ? 'bg-slate-50 text-slate-600 ring-slate-200' : e.ok === false ? 'bg-red-50 text-red-700 ring-red-200' : e.ok ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-amber-50 text-amber-700 ring-amber-200'}`}>
                  {e.pending ? <Loader2 className="h-3 w-3 animate-spin" /> : e.ok === false ? <XCircle className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />}
                  {e.label}
                </span>
              </div>
            ) : (
              <div key={e.id} className={`flex gap-2 ${e.kind === 'you' ? 'flex-row-reverse' : ''}`}>
                <span className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${e.kind === 'you' ? 'bg-slate-200 text-slate-600' : 'bg-blue-100 text-blue-700'}`}>
                  {e.kind === 'you' ? <User className="h-3.5 w-3.5" /> : <Bot className="h-3.5 w-3.5" />}
                </span>
                <p className={`max-w-[80%] rounded-[3px] px-3 py-2 text-[13px] leading-relaxed ${e.kind === 'you' ? 'bg-slate-100 text-slate-800' : 'bg-blue-50 text-slate-800'}`}>{e.text}</p>
              </div>
            ))}
            {error && <p className="rounded-[3px] bg-red-50 px-3 py-2 text-[12.5px] text-red-700 ring-1 ring-red-200">{error}</p>}
          </div>

          <footer className="flex items-center gap-2 border-t border-slate-100 px-4 py-3">
            {live ? (
              <>
                <div className="flex h-8 flex-1 items-end gap-[3px]" aria-hidden>
                  {Array.from({ length: 18 }).map((_, i) => (
                    <span key={i} className="w-1 rounded-full bg-blue-500/70 transition-[height] duration-100"
                      style={{ height: `${Math.max(12, Math.min(100, (muted ? 0 : level) * 260 * (0.5 + ((i * 37) % 10) / 10)))}%` }} />
                  ))}
                </div>
                <button type="button" onClick={() => { const m = !muted; setMuted(m); callRef.current?.setMuted(m) }}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50" aria-label={muted ? 'Unmute' : 'Mute'}>
                  {muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                </button>
                <button type="button" onClick={stop} className="rounded-[3px] bg-red-600 px-3 py-2 text-[12.5px] font-semibold text-white hover:bg-red-700">End</button>
              </>
            ) : (
              <button type="button" disabled={phase === 'connecting' || onCall} onClick={() => void start()}
                className="inline-flex w-full items-center justify-center gap-2 rounded-[3px] bg-blue-600 px-3 py-2.5 text-[13px] font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
                {phase === 'connecting' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic className="h-4 w-4" />}
                {phase === 'connecting' ? 'Connecting…' : 'Start'}
              </button>
            )}
          </footer>
        </div>
      )}

      <button type="button" onClick={() => setOpen((v) => !v)} aria-label="Voice assistant" title={onCall ? 'Not available during a call' : 'Voice assistant'}
        className={`fixed bottom-5 right-5 z-[880] flex h-14 w-14 items-center justify-center rounded-full text-white shadow-[0_10px_30px_-8px_rgba(37,99,235,0.6)] transition hover:scale-105 ${live ? 'bg-blue-600 ring-4 ring-blue-200' : 'bg-blue-600'}`}>
        {live && <span className="absolute inset-0 animate-ping rounded-full bg-blue-400/40" aria-hidden />}
        <AudioLines className="relative h-6 w-6" />
      </button>
    </>
  )
}
